import { ReactNode } from 'react';
import { DefaultValues } from 'react-hook-form';
import { z } from 'zod';
import { Pencil } from 'lucide-react';

import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { useToast } from '@/components/ui/hooks/use-toast';
import { getCoachesTqReview, updateCoachTqReviewStatus } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import {
  CoachTqReviewItem,
  CoachTqReviewStatus,
  CoachTqReviewStatusLabelMap,
  coachTqReviewStatusSchema,
} from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const editFormSchema = z.object({
  status: coachTqReviewStatusSchema,
});

type EditForm = z.infer<typeof editFormSchema>;

const renderStatus = (status: CoachTqReviewStatus) =>
  status === CoachTqReviewStatus.blank ? '—' : CoachTqReviewStatusLabelMap[status];

export interface EditTqReviewStatusProps {
  review: CoachTqReviewItem;
  trigger?: ReactNode;
}

export const EditTqReviewStatus = ({
  review,
  trigger = (
    <button type="button" className="p-0.5 text-foreground hover:text-foreground/80" aria-label="Edit review status">
      <Pencil size={14} />
    </button>
  ),
}: EditTqReviewStatusProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({ mutationFn: updateCoachTqReviewStatus });

  const onSubmit = async ({ formData, closeModal }: SubmitProps<EditForm>) => {
    try {
      await mutateAsync({
        reviewId: review.id,
        data: { status: formData.status },
      });

      await queryClient.invalidateQueries({ queryKey: getCoachesTqReview().queryKey });

      toast({ description: 'Review status updated successfully' });
      closeModal();
    } catch (e: unknown) {
      toast({
        description: airServerErrorFrom(e)?.userFacingMessage ?? 'Failed to update review status. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const defaultValues: DefaultValues<EditForm> = {
    status: review.status,
  };

  return (
    <FormModal
      modalTitle="Review status"
      formSchema={editFormSchema}
      onSubmit={onSubmit}
      trigger={trigger}
      defaultValues={defaultValues}
      confirmBtnLabel="Save"
      cancelBtnLabel="Cancel"
      shouldResetOnCancel
      resetOnDefaultValuesChange
      dialogContentProps={{ className: 'max-w-md lg:max-w-md' }}
    >
      {({ form }) => (
        <div className="py-4">
          <FormSingleSelect
            name="status"
            label="Review status"
            form={form}
            options={coachTqReviewStatusSchema.options}
            renderOption={renderStatus}
            renderOptionSelected={renderStatus}
            triggerProps={{ className: 'w-full' }}
          />
        </div>
      )}
    </FormModal>
  );
};
